import { QueryClient } from "@tanstack/react-query";
import { QueryKeyGenerator } from "./actors/generator.service";
import { QueryKeyInvalidator } from "./actors/invalidator.service";
import { QueryKeyRefetcher } from "./actors/refetcher.service";
import { queryConfig } from "../client";

/**
 * Unified singleton service to generate, invalidate, and refetch queries using "@tanstack/react-query".
 *
 * This service acts as a wrapper for the individual query key services (generator, invalidator, refetcher)
 * and provides both traditional method calls and builder pattern access.
 *
 * The order of the items which compose the queryKey is important. These items should go from generic to specific.
 * Invalidation and refetching can be done by matching only a partial part of the key.
 *
 * @example
 * // Access through container
 * import { container } from '@/services/container/container.service';
 * const queryKeyService = container.queryKey;
 *
 * // Traditional method calls
 * const key = queryKeyService.generate('/home', 'en', 'user', ['profile'], 123);
 * queryKeyService.invalidate('/home', 'en', 'user');
 * queryKeyService.refetch('/home', 'en', 'user', ['profile']);
 *
 * // Builder pattern access
 * const key2 = queryKeyService.generator()
 *   .setPage('/home')
 *   .setLocale('en')
 *   .setGroup('user')
 *   .setScopes(['profile'])
 *   .setId(123)
 *   .build();
 *
 * queryKeyService.invalidator()
 *   .setPage('/home')
 *   .setLocale('en')
 *   .setGroup('user')
 *   .execute();
 *
 * queryKeyService.refetcher()
 *   .setPage('/home')
 *   .setLocale('en')
 *   .setGroup('user')
 *   .setScopes(['profile'])
 *   .execute();
 */
class QueryKey {
    private static _instance: QueryKey | undefined = undefined;
    private _queryClient: QueryClient;

    /**
     * Private constructor for QueryKey service.
     *
     * @param {QueryClient} queryClient - The query client instance from @tanstack/react-query.
     */
    private constructor(queryClient: QueryClient) {
        this._queryClient = queryClient;
    }

    /**
     * Initialize the QueryKey singleton instance.
     *
     * @param {QueryClient} queryClient - The query client instance from @tanstack/react-query.
     */
    public static initialize(queryClient: QueryClient = new QueryClient(queryConfig)): void {
        if (!this._instance) {
            this._instance = new QueryKey(queryClient);
        }
    }

    /**
     * Get the singleton instance of QueryKey.
     *
     * @returns {QueryKey} The singleton instance.
     * @throws {Error} If the instance is not initialized.
     */
    public static get instance(): QueryKey {
        if (!this._instance) {
            throw new Error("QueryKey is not initialized. Please call initialize() first.");
        }
        return this._instance;
    }

    /**
     * Get the query client instance.
     *
     * @returns {QueryClient} The query client instance.
     */
    public get generator(): QueryKeyGenerator {
        return new QueryKeyGenerator();
    }

    /**
     * Get the query invalidator instance.
     *
     * @returns {QueryKeyInvalidator} The query invalidator instance.
     */
    public get invalidator(): QueryKeyInvalidator {
        return new QueryKeyInvalidator(this._queryClient);
    }

    /**
     * Get the query refetcher instance.
     *
     * @returns {QueryKeyRefetcher} The query refetcher instance.
     */
    public get refetcher(): QueryKeyRefetcher {
        return new QueryKeyRefetcher(this._queryClient);
    }
}

QueryKey.initialize();
export const queryKey = QueryKey.instance;
export default QueryKey;
